import { useCallback, useMemo, useState } from 'react'

import { useLazyQuery } from '@apollo/client'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_COMPANY_LEAD_SOURCES } from 'src/graphql/trackinglinks.graphql'
import {
  buildLeadSourceOptions,
  CompanyLeadSource,
  LeadSourceOption,
} from 'src/lib/leadSource.utils'

interface UseCompanyLeadSourceOptions {
  /** Nested option tree: in-use link types → their distinct link names. */
  options: LeadSourceOption[]
  /** Lazily triggers the fetch; safe to call repeatedly (guarded). */
  loadData: () => void
  loading: boolean
  isLoaded: boolean
}

/**
 * Fetches the company's distinct in-use lead sources (companyLeadSources) and
 * shapes them into the nested option tree used by both the Applicants and
 * Pipeline Analytics lead-source filters. Loads lazily via loadData so the
 * query only fires when the filter is actually opened.
 */
export const useCompanyLeadSourceOptions = (): UseCompanyLeadSourceOptions => {
  const [sources, setSources] = useState<CompanyLeadSource[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  // Tracks a failed fetch so we can (a) stop showing "Loading…" and (b) still
  // allow a retry the next time the submenu is opened.
  const [hasErrored, setHasErrored] = useState(false)

  const onError = useCallback(() => {
    toast('Failed to load lead sources', 'error')
    // Mark as loaded so the menu stops showing "Loading…" after a failure —
    // the toast already surfaced the error and options simply stay empty.
    setIsLoaded(true)
    setHasErrored(true)
  }, [])

  const [loadLeadSources, { loading }] = useLazyQuery(GET_COMPANY_LEAD_SOURCES, {
    onCompleted: (data) => {
      setSources(data?.companyLeadSources || [])
      setIsLoaded(true)
      setHasErrored(false)
    },
    onError,
  })

  // Call on submenu open (an event, never during render): fetches on first
  // open and re-fetches on reopen if the previous attempt failed. A successful
  // load is cached (no-op on subsequent opens).
  const loadData = useCallback(() => {
    if (loading) return
    if (isLoaded && !hasErrored) return
    setHasErrored(false)
    setIsLoaded(false) // show the loading state again while (re)fetching
    loadLeadSources()
  }, [loading, isLoaded, hasErrored, loadLeadSources])

  const options = useMemo(() => buildLeadSourceOptions(sources), [sources])

  return { options, loadData, loading, isLoaded }
}
